import {IPoint} from "../interfaces/IPoint";
import {IStop} from "../interfaces/IStop";
import {Triangulation} from "../Tools/Triangulation";
import {Geodesic} from "..";
import {ILeg} from "../interfaces/ILeg";
import {ILegBuilderStopProjection} from "../interfaces/ILegBuilderStopProjection";
import {ILegBuilderDirectionProjection} from "../interfaces/ILegBuilderDirectionProjection";
import {IDirection} from "../interfaces/IDirection";
import {Vector} from "../entities/Vector";

export class LegBuilder {
    
    private pointList: IPoint[];
    // private stops: IStop[] = [];
    private minIncludedIndex: number;
    private maxIncludedIndex: number;
    private triangulation: Triangulation;
    private distList: number[];
    private distanceOffset: number;
    public length: number;
    public legIndex: number;
    private containedStops: {projection: ILegBuilderStopProjection, unsure: boolean}[] = [];
    private containedDirections: {projection: ILegBuilderDirectionProjection, unsure: boolean}[] = [];
    private c_refPointList: IPoint[];   //Reference to the initial point list (for deep copy purpose)
    private c_loops: IPoint[][];        //Reference to the initial loops (for deep copy purpose)
    
    
    constructor(refPointList: IPoint[], loops: IPoint[][], index: number) {
        this.c_refPointList = refPointList;
        this.c_loops = loops;
        this.legIndex = index;
        this.pointList = loops[index];
        this.distList = Geodesic.distanceArray(this.pointList);
        this.triangulation = new Triangulation(this.pointList);   
        
        let indexOffset = 0;
        for(let i = 0; i < index; i++) {
            indexOffset += loops[i].length - 1; //Important since loops have the first point of the previous loop included in them.
        }
        this.minIncludedIndex = indexOffset;
        this.maxIncludedIndex = indexOffset + loops[index].length - 1;
        this.distanceOffset = index === 0 ? 0 : Geodesic.pointListMetersLen(refPointList.slice(0, this.minIncludedIndex + 1));
        this.length = Geodesic.pointListMetersLen(refPointList.slice(this.minIncludedIndex, this.maxIncludedIndex + 1));
    }

//DirectionsHandling. Bytecode builder can not resolve certainty by itself

    projectDirection(direction: IDirection) : ILegBuilderDirectionProjection {
        let interpolation = this.triangulation
            .interpolatePoint(direction.point);
        let dist = this.distList[interpolation.localRefIndex] + this.distanceOffset;

        if(interpolation.relativePosition === "BEFORE") {
            dist = dist - Geodesic.metersBetween(interpolation.refPoint, interpolation.interpolation);
        } else if(interpolation.relativePosition === "AFTER") {
            dist = dist + Geodesic.metersBetween(interpolation.refPoint, interpolation.interpolation);
        }
        return {
            completeDistance: dist,
            gap: interpolation.iGap,
            legIndex: this.legIndex,
            refLat: interpolation.refPoint.lat,
            refLon: interpolation.refPoint.lon,
            direction: direction,
        };
    }

    addDirectionProjectionToLeg(directionProjection : ILegBuilderDirectionProjection, unsure: boolean) {
        this.containedDirections.push({projection: directionProjection, unsure: unsure});
    }


    getDirectionProjection(direction_id: string) : {projection: ILegBuilderDirectionProjection, unsure: boolean}|null {
        let search = this.containedDirections.find(e => e.projection.direction.id === direction_id);
        return search === undefined ? null : search;
    }

    hasDirection(id:string) : boolean {
        return !!this.containedDirections.filter(e => e.projection.direction.id === id).length;
    }

    hasStop(id: string) {
        return !!this.containedStops.filter(e => e.projection.stop.id === id).length;
    }

    removeDirectionProjectionFromLeg(direction_id: string) {
        let tmp = this.containedDirections.slice();
        this.containedDirections = [];
        for(let e of tmp) {
            if(e.projection.direction.id !== direction_id) {
                this.containedDirections.push(e);
            }
        }
    }


//Stop projections handling.


    projectStop(stop: IStop) : ILegBuilderStopProjection {
        let interpolation = this.triangulation.interpolatePoint(stop.point);
        let dist = this.distList[interpolation.localRefIndex] + this.distanceOffset;
        if(interpolation.relativePosition === "BEFORE") {
            dist = dist - Geodesic.metersBetween(interpolation.refPoint, interpolation.interpolation);
        } else if(interpolation.relativePosition === "AFTER") {
            dist = dist + Geodesic.metersBetween(interpolation.refPoint, interpolation.interpolation);
        }
        return {
            completeDistance: dist,
            gap: interpolation.iGap,
            legIndex: this.legIndex,
            refLat: interpolation.refPoint.lat,
            refLon: interpolation.refPoint.lon,
            stop: stop,
        };
    }

    addStopProjectionToLeg(stopProjection : ILegBuilderStopProjection, unsure: boolean) {
        this.containedStops.push({projection: stopProjection, unsure: unsure});
    }

    removeStopProjectionFromLeg(stop_id: string) {
        let tmp = this.containedStops.slice();
        this.containedStops = [];
        for(let e of tmp) {
            if(e.projection.stop.id !== stop_id) {
                this.containedStops.push(e);
            }
        }
    }

    updateStopCertainty(stop_id: string, unsure: boolean) {
        let elementSC = this.containedStops.find(e => e.projection.stop.id === stop_id);
        if(elementSC) {
            this.containedStops[this.containedStops.indexOf(elementSC)].unsure = unsure;
        }
    }

    updateDirectionCertainty(direction_id: string, unsure: boolean) {
        let elementSC = this.containedDirections.find(e => e.projection.direction.id === direction_id);
        if(elementSC) {
            this.containedDirections[this.containedDirections.indexOf(elementSC)].unsure = unsure;
        }
    }

    getStopProjection(stop_id: string) : {projection: ILegBuilderStopProjection, unsure: boolean}|null {
        let search = this.containedStops.find(e => e.projection.stop.id === stop_id);
        return search === undefined ? null : search;
    }

    getSureStopsProjections() : ILegBuilderStopProjection[] {
        return this.containedStops.filter(e => !e.unsure).map(e => e.projection);
    }

    getUnSureStopsProjections() : ILegBuilderStopProjection[] {
        return this.containedStops.filter(e => e.unsure).map(e => e.projection);
    }


    /**
     * Creates a deep copy of a leg, useful to rollback when unable to solve stop positioning.
     */
    deepCopy() : LegBuilder {
        let copy = new LegBuilder(this.c_refPointList, this.c_loops, this.legIndex);
        for(let containedStop of this.containedStops) {
            copy.addStopProjectionToLeg(copy.projectStop(containedStop.projection.stop), containedStop.unsure);
        }

        return copy;
    }


    build() : ILeg {
        return {
            firstIndexOnRawTriangulation: this.minIncludedIndex,
            lastIndexOnRawTriangulation: this.maxIncludedIndex,
            containedStops: this.containedStops.map((e) => { return {distanceFromOrigin: e.projection.completeDistance, refLat: e.projection.refLat, refLon: e.projection.refLon, stopId: e.projection.stop.id, unSure: e.unsure}}),
            containedDirections: this.containedDirections.map((e) => { return {distanceFromOrigin: e.projection.completeDistance, refLat: e.projection.refLat, refLon: e.projection.refLon, directionId: e.projection.direction.id, unSure: e.unsure}}),
            distanceFromOrigin: this.distanceOffset,
            length: this.length
        }
    }


    /**
     * From a point which MUST be close to the track (gap < MAX_DIRECTION_GAP)
     * @param point
     */
    computeHeadingStd(point: IPoint) {
        let i = this.triangulation.interpolatePoint(point).localRefIndex;
        if(i === 0 || i === this.triangulation.pointList.length) {
            return 0;
        } else {
            let h1 = Geodesic.computeHeading(new Vector(this.triangulation.pointList[i-1], this.triangulation.pointList[i]));
            let h2 = Geodesic.computeHeading(new Vector(this.triangulation.pointList[i], this.triangulation.pointList[i+1]));
            return Geodesic.getStandardDeviation([h1,h2]);
        }
    }
}
